import { RefObject, useLayoutEffect, useState } from 'react'

/**
 * A simple hook to return if an element's content is overflowing.
 * Currently, supports only horizontal overflow, but could be modified in the future to return
 * for both horizontal and vertical if the use case arrives
 * Based on: https://www.robinwieruch.de/react-custom-hook-check-if-overflow/
 * @param ref The value to debounce
 * @returns boolean that is true if the content of `ref` is overflowing its container
 */
export const useIsOverflowing = <T extends HTMLElement>(ref: RefObject<T | null>) => {
  const [isOverflow, setIsOverflow] = useState<boolean | undefined>(undefined)

  useLayoutEffect(() => {
    const { current } = ref

    const trigger = () => {
      const hasOverflow = current ? current.scrollWidth > current.clientWidth : undefined

      setIsOverflow(hasOverflow)
    }

    if (current) {
      if ('ResizeObserver' in window) {
        new ResizeObserver(trigger).observe(current)
      }

      trigger()
    }
  }, [ref, ref.current?.textContent])

  return isOverflow
}
